import { HubspotForm } from '@/components/common/hubspot-form';
import { createIntlTranslations } from 'Features/Intl/fns';
import { Layout } from 'Features/Shared/Components/Layout';
import { EstimatorCTA } from 'Features/Shared/Components/UI/EstimatorCTA';
import { ProseContent } from 'Features/Shared/Components/UI/ProseContent';
import { GetStaticPathsContext, GetStaticPropsContext } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { MDXRemote, MDXRemoteSerializeResult } from 'next-mdx-remote';
import { serialize } from 'next-mdx-remote/serialize';
import { NextSeo } from 'next-seo';
import Script from 'next/script';
import fs from 'node:fs/promises';
import path from 'node:path';
import { TbBuilding, TbHelpHexagon, TbHome, TbSearch, TbSeeding } from 'react-icons/tb';
import remarkGfm from 'remark-gfm';

export default function Page({
  mdx,
}: {
  mdx: MDXRemoteSerializeResult<Record<string, unknown>, Record<string, string>>;
}) {
  return (
    <Layout>
      <NextSeo title={mdx.frontmatter.title} description={mdx.frontmatter.subtitle} />

      <div className="max-w-7xl mx-auto min-h-[calc(100vh-6rem)] px-6">
        <div className="mx-auto max-w-2xl py-32 sm:py-24">
          <div className="text-center">
            <h1 className="text-4xl font-space font-bold tracking-tight text-slate-900 sm:text-6xl">
              {mdx.frontmatter.title}
            </h1>
            <p className="mt-6 text-lg leading-8 text-slate-600">{mdx.frontmatter.subtitle}</p>
          </div>
        </div>

        <MDXRemote
          {...mdx}
          components={{
            TbSeeding,
            TbBuilding,
            TbHome,
            TbSearch,
            TbHelpHexagon,
            HubspotForm,
            EstimatorCTA,
            ProseContent,
            Script,
          }}
        />
      </div>
    </Layout>
  );
}

export async function getStaticProps(context: GetStaticPropsContext) {
  const locale = context.locale?.startsWith('it') ? 'it' : 'en';
  const intl = await createIntlTranslations(locale);
  const i18n = await serverSideTranslations(locale);

  if (!context.params?.slug) {
    return { notFound: true };
  }

  const filePath = Array.isArray(context.params.slug)
    ? context.params.slug.join('/')
    : context.params.slug;

  const res = await fs.readFile(`./content/${locale}/${filePath}.mdx`, 'utf-8');
  const mdx = await serialize(res, {
    parseFrontmatter: true,
    mdxOptions: { remarkPlugins: [remarkGfm] },
  });

  return {
    props: { mdx, ...i18n, ...intl },
  };
}

async function readDirRecursive(directoryPath: string, filesList: string[] = []) {
  const files = await fs.readdir(directoryPath);

  for (const file of files) {
    const filePath = path.join(directoryPath, file);
    const stats = await fs.stat(filePath);

    if (stats.isDirectory()) {
      // Recursively read the subdirectory
      await readDirRecursive(filePath, filesList);
    } else {
      // Add the file path to the list
      filesList.push(filePath);
    }
  }

  return filesList;
}

export async function getStaticPaths({ locales = [] }: GetStaticPathsContext) {
  const items: Array<any> = [];

  // Building paths for static contents
  for (const locale of locales) {
    const startDir = `content/${locale}`;
    const files = await readDirRecursive(startDir);

    for (const file of files) {
      const slugPath = file.replace(startDir, '').replace('.mdx', '').split('/').filter(Boolean);
      items.push({ params: { slug: slugPath }, locale });
    }
  }

  return {
    paths: items,
    fallback: false,
  };
}
